import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FinancialPOAIntakeSchema,
  type FinancialPOAIntake,
} from '@bedside-docs/contracts';
import {
  createFinancialPOA,
  isCreateFinancialPOAError,
  type CreateFinancialPOAError,
} from '../../lib/api';
import { useFinancialPoaFormContext } from './FinancialPoaFormContext';
import { parseFinancialPoaIntakeCandidate } from './financialIntakeAdapter';
import {
  toFinancialPOAReviewViewModel,
  type FinancialPOAReviewViewModel,
} from './financialReviewViewModel';
import styles from './ReviewPOA.module.scss';

const DRAFT_STORAGE_KEYS = ['poa.financial.draft.v2', 'poa.financial.draft.v1'];
const LAST_RESULT_STORAGE_KEY = 'poa.financial.last.v1';

const RETURN_TO_FORM_MESSAGE =
  'We could not find your saved answers. Please return to the form to continue.';

type AlertState =
  | { kind: 'load'; message: string }
  | { kind: 'validation'; issues: string[] }
  | { kind: 'api_invalid'; issues: string[] }
  | { kind: 'api_forbidden' }
  | { kind: 'api_rate_limited' }
  | { kind: 'api_server'; message?: string };

type StepIdForFocus =
  | 'principal_info'
  | 'agent_info'
  | 'authority_grant'
  | 'effective_and_termination';

type FocusLinkState = {
  focusRequest: { stepId: StepIdForFocus; selector: string };
};

const normalizeIssues = (issues: string[]) =>
  issues.filter((issue) => issue && issue.trim().length > 0);

const mapZodIssues = (result: ReturnType<typeof parseFinancialPoaIntakeCandidate>) => {
  if (result.success) {
    return null;
  }

  return normalizeIssues(result.error.issues.map((issue) => issue.message));
};

const ReviewPOA = () => {
  const navigate = useNavigate();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const formContext = useFinancialPoaFormContext();
  const [intake, setIntake] = useState<FinancialPOAIntake | null>(null);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    if (alert) {
      alertRef.current?.focus();
    }
  }, [alert]);

  useEffect(() => {
    if (intake) {
      return;
    }

    const contextIntake = formContext?.intake;
    if (contextIntake) {
      const parsed = FinancialPOAIntakeSchema.safeParse(contextIntake);
      if (parsed.success) {
        setIntake(parsed.data);
        setAlert(null);
        setHasAttemptedLoad(true);
        return;
      }
    }

    if (hasAttemptedLoad) {
      return;
    }

    if (typeof window === 'undefined') {
      setAlert({
        kind: 'load',
        message: RETURN_TO_FORM_MESSAGE,
      });
      setHasAttemptedLoad(true);
      return;
    }

    let validationIssues: string[] | null = null;

    for (const key of DRAFT_STORAGE_KEYS) {
      const raw = window.localStorage.getItem(key);
      if (!raw) {
        continue;
      }

      try {
        const parsedValue = JSON.parse(raw) as unknown;
        const result = parseFinancialPoaIntakeCandidate(parsedValue);
        if (result.success) {
          setIntake(result.data);
          setAlert(null);
          setHasAttemptedLoad(true);
          return;
        }

        const issues = mapZodIssues(result);
        if (issues && issues.length > 0) {
          validationIssues = issues;
        }
      } catch (error) {
        console.warn('Failed to parse stored financial POA draft', error);
      }
    }

    setHasAttemptedLoad(true);
    if (validationIssues && validationIssues.length > 0) {
      setAlert({ kind: 'validation', issues: validationIssues });
    } else {
      setAlert({ kind: 'load', message: RETURN_TO_FORM_MESSAGE });
    }
  }, [formContext?.intake, hasAttemptedLoad, intake]);

  const viewModel = useMemo<FinancialPOAReviewViewModel | null>(() => {
    if (!intake) {
      return null;
    }

    return toFinancialPOAReviewViewModel(intake);
  }, [intake]);

  const handleReturnToForm = () => {
    navigate('/poa/financial/form');
  };

  const persistResult = (result: {
    id: string;
    expiresAt: number;
    pdfUrl: string;
  }) => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(
        LAST_RESULT_STORAGE_KEY,
        JSON.stringify(result),
      );
    } catch (error) {
      console.warn('Failed to store financial POA result metadata', error);
    }
  };

  const handleGenerate = async () => {
    if (!intake) {
      return;
    }

    setAlert(null);
    setIsGenerating(true);

    try {
      const result = await createFinancialPOA(intake);
      persistResult({
        id: result.id,
        expiresAt: result.expiresAt,
        pdfUrl: result.pdfUrl,
      });

      navigate(`/poa/financial/share?id=${encodeURIComponent(result.id)}`);
    } catch (error) {
      if (isCreateFinancialPOAError(error)) {
        handleApiError(error);
      } else if (error instanceof Error) {
        setAlert({
          kind: 'api_server',
          message: error.message,
        });
      } else {
        setAlert({ kind: 'api_server' });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApiError = (error: CreateFinancialPOAError) => {
    switch (error.type) {
      case 'invalid_input': {
        const issues = normalizeIssues(error.issues);
        setAlert({
          kind: 'api_invalid',
          issues:
            issues.length > 0
              ? issues
              : ['Please double-check the form for missing information.'],
        });
        break;
      }
      case 'forbidden':
        setAlert({ kind: 'api_forbidden' });
        break;
      case 'rate_limited':
        setAlert({ kind: 'api_rate_limited' });
        break;
      case 'server_error':
        setAlert({ kind: 'api_server', message: error.message });
        break;
      default:
        setAlert({ kind: 'api_server' });
    }
  };

  const isGenerateDisabled = isGenerating || !intake;

  return (
    <main className={styles.page} role="main">
      <div className={styles.inner}>
        <h1 className={styles.title} ref={headingRef} tabIndex={-1}>
          Review your financial power of attorney
        </h1>

        {alert && (
          <div
            className={styles.alert}
            role="alert"
            tabIndex={-1}
            ref={alertRef}
          >
            {alert.kind === 'validation' && (
              <AlertValidation issues={alert.issues} onReturn={handleReturnToForm} />
            )}
            {alert.kind === 'load' && (
              <AlertLoad message={alert.message} onReturn={handleReturnToForm} />
            )}
            {alert.kind === 'api_invalid' && (
              <AlertInvalid issues={alert.issues} onReturn={handleReturnToForm} />
            )}
            {alert.kind === 'api_forbidden' && (
              <div className={styles.alertContent}>
                <p>
                  We can&apos;t complete this request right now. Please return to
                  the form and try again.
                </p>
                <button
                  type="button"
                  onClick={handleReturnToForm}
                  className={styles.alertButton}
                >
                  Return to form
                </button>
              </div>
            )}
            {alert.kind === 'api_rate_limited' && (
              <div className={styles.alertContent}>
                <p>We&apos;re generating a lot of documents at once. Please try again in a moment.</p>
              </div>
            )}
            {alert.kind === 'api_server' && (
              <div className={styles.alertContent}>
                <p>
                  We couldn&apos;t generate your document right now.{' '}
                  {alert.message
                    ? alert.message
                    : 'Please try again shortly.'}
                </p>
              </div>
            )}
          </div>
        )}

        {viewModel && (
          <section className={styles.cards} aria-label="Document details">
            <ReviewCard
              title="Principal"
              editState={{ focusRequest: { stepId: 'principal_info', selector: '#principal-full-name' } }}
            >
              <p className={styles.cardDescription}>
                The Principal is the person this document is about.
              </p>
              <PersonDetails person={viewModel.principal} />
            </ReviewCard>

            <ReviewCard
              title="Agent"
              editState={{ focusRequest: { stepId: 'agent_info', selector: '#agent-full-name' } }}
            >
              <p className={styles.cardDescription}>
                The Agent is the trusted person who will act on the
                Principal’s behalf.
              </p>
              <PersonDetails person={viewModel.agent} />
              {viewModel.agent.successors.length > 0 && (
                <section className={styles.successors} aria-label="Successor agents">
                  <h3>Successor agents</h3>
                  <ul className={styles.successorList}>
                    {viewModel.agent.successors.map((successor, index) => (
                      <li key={`${successor.name}-${index}`}>
                        <PersonDetails person={successor} compact />
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </ReviewCard>

            <ReviewCard
              title="Powers &amp; Effectiveness"
              editState={{
                focusRequest: {
                  stepId: 'authority_grant',
                  selector: 'input[name="power-realEstateTransactions"]',
                },
              }}
            >
              <section className={styles.powersSection}>
                <h3>Granted powers</h3>
                <ul>
                  {viewModel.powers.granted.map((item) => (
                    <li key={item.key}>{item.label}</li>
                  ))}
                </ul>
                <p className={styles.effectiveness}>{viewModel.powers.effectivenessSummary}</p>
                {viewModel.powers.terminationConditions && (
                  <p className={styles.note}>
                    Ends if: {viewModel.powers.terminationConditions}
                  </p>
                )}
                {viewModel.powers.specialInstructions && (
                  <section className={styles.specialInstructions}>
                    <h4>Special instructions</h4>
                    <p>{viewModel.powers.specialInstructions}</p>
                  </section>
                )}
              </section>
            </ReviewCard>

          </section>
        )}

        {viewModel && (
          <>
            <p className={styles.nextStepNote}>
              On the next page you&apos;ll have the option to download or email the
              document.
            </p>
            <p className={styles.retentionNote}>
              Files are stored privately for 30 days before they are removed.
              The download link will expire in ~1 hour.
            </p>
          </>
        )}

        <div className={styles.actions} aria-busy={isGenerating}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleReturnToForm}
            disabled={isGenerating}
          >
            Back
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleGenerate}
            disabled={isGenerateDisabled}
            aria-busy={isGenerating}
          >
            {isGenerating && (
              <span className={styles.spinner} aria-hidden="true" />
            )}
            <span>{isGenerating ? 'Generating…' : 'Generate Document'}</span>
          </button>
        </div>
      </div>

      <footer className={styles.footer} role="contentinfo" />
    </main>
  );
};

const ReviewCard = ({
  title,
  children,
  editState,
}: {
  title: string;
  children: ReactNode;
  editState: FocusLinkState;
}) => (
  <article className={styles.card}>
    <header className={styles.cardHeader}>
      <h2>{title}</h2>
      <Link to="/poa/financial/form" state={editState} className={styles.editLink}>
        Edit
      </Link>
    </header>
    <div className={styles.cardContent}>{children}</div>
  </article>
);

const PersonDetails = ({
  person,
  compact = false,
}: {
  person: FinancialPOAReviewViewModel['principal'];
  compact?: boolean;
}) => (
  <div className={compact ? styles.personCompact : styles.person}>
    <p className={styles.personName}>{person.name}</p>
    {person.dob && <p className={styles.personMeta}>Born {person.dob}</p>}
    <p className={styles.personMeta}>{person.phone}</p>
    {person.email && <p className={styles.personMeta}>{person.email}</p>}
    <address className={styles.address}>
      {person.addressLines.map((line, index) => (
        <span key={`${line}-${index}`}>{line}</span>
      ))}
    </address>
  </div>
);

const AlertLoad = ({
  message,
  onReturn,
}: {
  message: string;
  onReturn: () => void;
}) => (
  <div className={styles.alertContent}>
    <p>{message}</p>
    <button type="button" onClick={onReturn} className={styles.alertButton}>
      Return to form
    </button>
  </div>
);

const AlertValidation = ({
  issues,
  onReturn,
}: {
  issues: string[];
  onReturn: () => void;
}) => (
  <div className={styles.alertContent}>
    <p>We need a bit more information before we can generate your document:</p>
    <ul>
      {issues.map((issue, index) => (
        <li key={`${issue}-${index}`}>{issue}</li>
      ))}
    </ul>
    <button type="button" onClick={onReturn} className={styles.alertButton}>
      Return to form
    </button>
  </div>
);

const AlertInvalid = ({
  issues,
  onReturn,
}: {
  issues: string[];
  onReturn: () => void;
}) => (
  <div className={styles.alertContent}>
    <p>We weren&apos;t able to generate the document. Please review the following:</p>
    <ul>
      {issues.map((issue, index) => (
        <li key={`${issue}-${index}`}>{issue}</li>
      ))}
    </ul>
    <button type="button" onClick={onReturn} className={styles.alertButton}>
      Return to form
    </button>
  </div>
);

export default ReviewPOA;
